/**
 * Первый экран.
 *
 * Одно действие и ничего кроме: каталог, рейтинги и объяснение правил ждут
 * за входом. Продаёт игру не текст, а сами снимки — они медленно сменяются
 * на фоне и наезжают, как в документальном фильме.
 */
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useLandingPhotos } from "../api/queries";
import { useMediaQuery } from "../lib/useMediaQuery";
import { usePageMeta } from "../lib/usePageMeta";

/** Сколько кадр держится на экране, миллисекунды. */
const SLIDE_MS = 7000;

/** Больше пяти кадров не берём: остальные человек уже не увидит. */
const MAX_SLIDES = 5;

export function LandingPage() {
  const { t } = useTranslation();
  usePageMeta({});
  // Человек, попросивший систему убрать анимации, обычно делает это не из
  // вкусовых соображений: движение на весь экран у части людей вызывает
  // головокружение. Наезд кадра и плывущая сетка — ровно такое движение.
  const calm = useMediaQuery("(prefers-reduced-motion: reduce)");
  // Кадры выбирает редакция в панели модерации. Раньше сюда шли обложки
  // квестов из каталога — они меняются сами собой, и первое, что видит
  // незнакомый человек, зависело от того, чей квест последним прошёл проверку.
  const photos = useLandingPhotos();

  const shots = useMemo(
    () =>
      (photos.data?.photos ?? [])
        .map((photo) => photo.imageUrl)
        .filter((url): url is string => Boolean(url))
        .slice(0, MAX_SLIDES),
    [photos.data],
  );

  const [current, setCurrent] = useState(0);

  useEffect(() => {
    // Один кадр листать некуда, а при выключенной анимации смена картинок —
    // такое же движение, как наезд, и её тоже быть не должно.
    if (calm || shots.length < 2) return;

    const timer = setInterval(() => {
      setCurrent((index) => (index + 1) % shots.length);
    }, SLIDE_MS);
    return () => clearInterval(timer);
  }, [calm, shots.length]);

  return (
    <section className="relative flex flex-1 items-center justify-center px-5 py-24">
      <Backdrop shots={shots} current={current} calm={calm} />

      <div className="relative z-10 flex max-w-4xl flex-col items-center text-center">
        <p
          className="landing-enter text-xs uppercase tracking-[0.35em] text-amber-300
                     drop-shadow-[0_1px_10px_var(--hero-glow)]"
        >
          {t("landing.kicker")}
        </p>

        <h1
          className="landing-enter mt-5 font-sans text-[2.5rem] font-extrabold leading-[1.05]
                     tracking-tight text-ink-50 drop-shadow-[0_2px_20px_var(--hero-glow)]
                     sm:text-6xl"
          style={{ animationDelay: "80ms" }}
        >
          <Trans i18nKey="landing.title" components={{ accent: <span className="text-amber-400" /> }} />
        </h1>

        <p
          className="landing-enter mt-6 max-w-xl text-base leading-relaxed text-ink-100
                     drop-shadow-[0_1px_12px_var(--hero-glow)] sm:text-lg"
          style={{ animationDelay: "160ms" }}
        >
          {t("landing.lead")}
        </p>

        <div className="landing-enter mt-10" style={{ animationDelay: "240ms" }}>
          <Link to="/login" className="btn-play">
            {t("landing.play")}
          </Link>
        </div>
      </div>
    </section>
  );
}

/**
 * Подложка: снимки, сетка меридианов и затемнение.
 *
 * Пока каталог грузится, кадров нет — остаётся градиент, и первый экран всё
 * равно выглядит законченным, а не пустым.
 */
function Backdrop({ shots, current, calm }: { shots: string[]; current: number; calm: boolean }) {
  return (
    <div className="absolute inset-0 z-0 overflow-hidden" aria-hidden>
      {shots.map((url, index) => (
        <img
          key={url}
          src={url}
          alt=""
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[2000ms]
                      ease-out ${index === current ? "opacity-100" : "opacity-0"}
                      ${!calm && index === current ? "animate-kenburns" : ""}`}
        />
      ))}

      {/* Затемнение ровно настолько, чтобы читался текст: снимок здесь и
          продаёт игру, и топить его в черноте — значит остаться с пустым
          экраном. Сверху плотнее под шапку, снизу — под кнопку. */}
      <div className="absolute inset-0 bg-gradient-to-b from-ink-950/80 via-ink-950/45 to-ink-950/85" />
      <div className="absolute inset-0 bg-[image:var(--hero-radial)]" />

      <Meridians calm={calm} />

      {/* Виньетка по краям: убирает жёсткую границу кадра у краёв экрана. */}
      <div className="absolute inset-0 shadow-[var(--hero-vignette)]" />
    </div>
  );
}

/**
 * Сетка меридианов, медленно плывущая вбок.
 *
 * Намекает на карту, ради которой сюда и приходят, но остаётся фоном:
 * узнаваемая графика без единого слова.
 */
function Meridians({ calm }: { calm: boolean }) {
  return (
    <svg
      className={`absolute inset-0 h-full w-full opacity-[0.13] ${calm ? "" : "animate-drift"}`}
      viewBox="0 0 1200 600"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <g fill="none" stroke="#d99447" strokeWidth="0.8">
        {[0, 1, 2, 3, 4, 5, 6].map((row) => (
          <line key={`h${row}`} x1="0" y1={row * 100} x2="1200" y2={row * 100} />
        ))}
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((column) => (
          <path
            key={`v${column}`}
            d={`M${column * 100} 0 Q${column * 100 + (column - 6) * 14} 300 ${column * 100} 600`}
          />
        ))}
      </g>
    </svg>
  );
}
